import React from 'react';
import PropTypes from 'prop-types';
import { MdCircle } from 'react-icons/md';
import NotesTable from './NotesTable';
import * as useFetchNotesModule from './useFetchNotes'; // Import of the module to override it
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';
import { formatNumericDate } from '../../utils/dates';
import { getPriorityColor } from '../../utils/api/site';
import { formatPriority } from '../../utils/formatPriority';

// Mock Data
const generateMockNotes = (count) => {
  const notes = [];
  const getPriority = (index) => {
    if (index % 3 === 0) return 'high';
    if (index % 2 === 0) return 'medium';
    if (index % 5 === 0) return 'no_priority';
    return 'low';
  };
  const getPriorityLabel = (priority) => {
    if (priority === 'high') return 'High';
    if (priority === 'medium') return 'Medium';
    if (priority === 'no_priority') return 'No Priority';
    return 'Low';
  };
  for (let i = 1; i <= count; i += 1) {
    const priority = getPriority(i);
    notes.push({
      id: i,
      site_name: `Site ${i}`,
      title: `Note Title ${i}`,
      status: i % 2 === 0 ? 'Open' : 'Closed',
      priority: (
        <div className="flex space-x-2 items-center">
          <MdCircle size={16} color={getPriorityColor(priority)} />
          <p>{formatPriority(getPriorityLabel(priority))}</p>
        </div>
      ),
      timestamp: formatNumericDate(`2024-11-${String(i).padStart(2, '0')}`),
    });
  }
  return notes;
};

const mockNotes = generateMockNotes(20);

// Mock useFetchNotes
// eslint-disable-next-line no-import-assign, import/namespace
useFetchNotesModule.default = () => {
  return [mockNotes, () => {}];
};

export default {
  title: 'Components/Notes/NotesTable',
  component: NotesTable,
  parameters: {
    layout: 'fullscreen',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `NotesTable` component displays note records in a structured table format. It allows users to review and sort note details efficiently.\n\n',
      // When & how to use it?
      '\nUse the `NotesTable` component when a tabular representation of note records is needed. It supports sortable columns and integrates with backend APIs for dynamic data fetching.\n\n',
      // Current Usage
      '\nThe `NotesTable` component is currently used on the **[Notes page](http://localhost:6006/?path=/docs/pages-notespage--docs) to provide a detailed view of note records in a table format. It enables users to interact with data, sort it, and review key details like site name, title, status, priority, and date.\n\n',
      // Related Components
      `\n- **[Table](http://localhost:6006/?path=/docs/components-table--docs)**: The base table component used within NotesTable.
       \n- **useFetchNotes** (app/javascript/src/componentsV2/Notes/useFetchNotes.js): Custom hook for fetching note data.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    organizationId: {
      control: { type: 'number' },
      description: 'Organization ID used to fetch note records for the table.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    params: {
      control: { type: 'object' },
      description: 'Search and filter parameters for fetching data dynamically.\n\n',
      table: {
        type: { summary: 'object' },
        required: true,
      },
    },
    currentPage: {
      control: { type: 'number' },
      description: 'Current page number for paginated data.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    itemsPerPage: {
      control: { type: 'number' },
      description: 'Number of items displayed per page.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    setTotalNotes: {
      control: { type: 'function' },
      description: 'Callback function to set the total number of notes for pagination.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
    onSort: {
      control: { type: 'function' },
      description: 'Callback function triggered when sorting columns.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
    onClick: {
      control: { type: 'function' },
      description: 'Callback function triggered when clicking on a table row.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
  },
};

const NotesTableTemplate = ({
  organizationId,
  params,
  currentPage,
  itemsPerPage,
  setTotalNotes,
  onSort,
  onClick,
}) => (
  <NotesTable
    organizationId={organizationId}
    params={params}
    currentPage={currentPage}
    itemsPerPage={itemsPerPage}
    setTotalNotes={setTotalNotes}
    onSort={onSort}
    onClick={onClick}
  />
);

NotesTableTemplate.propTypes = {
  organizationId: PropTypes.number.isRequired,
  params: PropTypes.object.isRequired,
  currentPage: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  setTotalNotes: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
};

const Template = NotesTableTemplate.bind({});

export const DefaultNotesTable = Template.bind({});
DefaultNotesTable.args = {
  organizationId: 1,
  params: {},
  currentPage: 1,
  itemsPerPage: 10,
  setTotalNotes: () => {},
  onSort: () => {},
  onClick: () => {},
};
DefaultNotesTable.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used when the user toggles table view button on the **[Notes page](http://localhost:6006/?path=/docs/pages-notespage--docs) to display note records in a structured table format. It shows sortable columns for details like site name, title, status, priority, and date. In Storybook, dummy data is displayed for component review purposes because API calls are not possible.\n\n',
  // Where is it currently used on the product?
  '\nThe `NotesTable` is currently used on the Notes page for displaying note records in a sortable table format.\n\n',
);
